// client/src/pages/secure/LicenseTrackingPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';
import { getCurrentUser } from '../../hooks/useCurrentUser';

const DAYS_SOON_THRESHOLD = 90;

const expirationStatus = (dateStr) => {
    if (!dateStr) return { label: 'No date on file', className: 'bg-gray-100 text-gray-800' };
    const days = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, className: 'bg-red-100 text-red-800' };
    if (days <= DAYS_SOON_THRESHOLD) return { label: `Expires in ${days}d`, className: 'bg-yellow-100 text-yellow-800' };
    return { label: `Expires ${new Date(dateStr).toLocaleDateString()}`, className: 'bg-green-100 text-green-800' };
};

// The bucket a row falls in, for the filter and the CSV.
const statusBucket = (dateStr) => {
    if (!dateStr) return 'No date on file';
    const days = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'Expired';
    if (days <= DAYS_SOON_THRESHOLD) return 'Expires within 90 days';
    return 'Current';
};

const LicenseTrackingPage = () => {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const apiClient = useApiClient();

    const fetchSummary = useCallback(() => {
        setIsLoading(true);
        apiClient.get('/api/ce/summary')
            .then(data => setRows(data))
            .catch(err => setError(err.message || 'Failed to load license/CE summary.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    // O11: the server returns the whole office only to the managing broker and office manager.
    const u = getCurrentUser();
    const wholeOffice = !!(u && (u.isManagingBroker || u.staffRole === 'Office Manager' || u.role === 'examiner'));

    if (isLoading) return <div className="p-8">Loading license &amp; CE summary...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;

    return (
        <div className="p-4 md:p-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">{wholeOffice ? 'License & CE Tracking' : 'My License & CE'}</h1>
            <p className="text-slate-500 mb-6 text-sm">{wholeOffice
                ? 'Every licensee and employee: licence expiry and CE hours.'
                : 'Your own licence and CE hours. The office-wide list is kept by the managing broker and office manager.'}</p>
            <div className="bg-white rounded-lg shadow p-4 md:p-6">
                <DataList rows={rows} rowKey={(r) => `${r.person_type}-${r.person_id}`} csvName="licence-ce" defaultSort={{ key: 'license_expiration_date', dir: 'asc' }}
                    filters={[{ key: 'person_type', label: 'Type' }, { key: 'status_label', label: 'Licence', options: ['Expired', 'Expires within 90 days', 'Current', 'No date on file'] }]}
                    empty="No agents or employees on file."
                    columns={[
                        { key: 'name', label: 'Name', render: (row) => <Link to={row.person_type === 'agent' ? `/dashboard/agents/${row.person_id}` : `/dashboard/employees/${row.person_id}`} className="text-indigo-600 hover:text-indigo-900">{row.name}</Link> },
                        { key: 'person_type', label: 'Type' },
                        { key: 'license_number', label: 'License #' },
                        { key: 'license_expiration_date', label: 'Expires', type: 'date' },
                        { key: 'status_label', label: 'Licence', value: (row) => statusBucket(row.license_expiration_date), render: (row) => { const st = expirationStatus(row.license_expiration_date); return <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${st.className}`}>{st.label}</span>; } },
                        { key: 'total_ce_hours', label: 'CE hours', type: 'number' },
                        { key: 'renew', label: '', sortable: false, csv: false, search: false, render: (row) => (row.person_type === 'agent' && row.license_expiration_date && statusBucket(row.license_expiration_date) !== 'Current'
                            ? <Link to={`/dashboard/desk-drafts?kind=license_renewal&agent_id=${row.person_id}`} className="text-indigo-600 text-xs">Draft renewal reminder</Link> : null) },
                    ]} />
            </div>
        </div>
    );
};

export default LicenseTrackingPage;
